/**
 * CertificateForm - Add certificate with searchable dropdown
 * FT-12: Accepts free text for custom certificate names not in predefined list
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check, PenLine } from 'lucide-react';
import { CERTIFICATE_TYPES } from '../../../../../config/crewRequirements';
import { normalizeCertificateExpiry } from './certificateHelpers';

// Certificate categories for grouping
// FT-125 Bug 5: Changed label to 'Mandatory Safety Training' (no count)
// FT-125 Bug 1: Keep PSSR in dropdown (crew may hold it) but not mandatory
const CERTIFICATE_CATEGORIES = {
  'Mandatory Safety Training': [
    'Personal Survival Techniques',
    'Fire Prevention & Fire Fighting',
    'Elementary First Aid',
    'Personal Safety & Social Responsibilities (PSSR)'
  ],
  'Medical Certificates': [
    'ENG1 Medical Certificate',
    'ML5 Medical Certificate'
  ],
  'Certificates of Competency': [
    'STCW Master (Workboat <500GT)',
    'STCW Mate (Workboat <500GT)',
    'Boatmaster Tier 1 Level 2',
    'Boatmaster Tier 2',
    'MCA Master <200GT',
    'MCA Mate <200GT',
    'MCA OOW <3000GT'
  ],
  'Radio Certificates': [
    'GMDSS GOC',
    'GMDSS ROC',
    'SRC (Short Range Certificate)'
  ],
  'Additional Training': [
    'HELM (Human Element Leadership & Management)',
    'Crowd Management',
    'Crisis Management',
    'Passenger Safety',
    'Security Awareness',
    'Ship Security Officer'
  ],
  'Engine Qualifications': [
    'AEC Level 1',
    'AEC Level 2',
    'STCW Engineer (Workboat)',
    'MCA Chief Engineer <3000kW',
    'MCA Second Engineer <3000kW'
  ],
  'Specialist Certificates': [
    'DP Certificate',
    'Oil Tanker Familiarization',
    'Chemical Tanker Familiarization',
    'High Speed Craft'
  ]
};

const CertificateForm = ({
  onAddCertificate,
  onCancel,
  existingCertificates = [],
  prefilledType = null,
  onClearPrefill = null
}) => {
  const [newCertificate, setNewCertificate] = useState({
    type: prefilledType || '',
    number: '',
    issue_date: '',
    expiry_date: '',
    issued_by: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // FT-12 Bug 4: Custom name for "Other" certificate type
  const [customCertName, setCustomCertName] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Set prefilled type when prop changes
  useEffect(() => {
    if (prefilledType) {
      setNewCertificate(prev => ({ ...prev, type: prefilledType }));
      setSearchTerm('');
      setIsDropdownOpen(false);
    }
  }, [prefilledType]);

  // Filter certificates based on search
  const filteredCertificates = useMemo(() => {
    if (!searchTerm) return CERTIFICATE_TYPES;
    const term = searchTerm.toLowerCase();
    return CERTIFICATE_TYPES.filter(cert =>
      cert.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Group filtered results by category
  const groupedResults = useMemo(() => {
    const groups = {};
    Object.entries(CERTIFICATE_CATEGORIES).forEach(([category, certs]) => {
      const filtered = certs.filter(cert =>
        filteredCertificates.includes(cert) &&
        !existingCertificates.some(ec => ec.type === cert)
      );
      if (filtered.length > 0) {
        groups[category] = filtered;
      }
    });

    // Add "Other" category for uncategorized certs
    const categorizedCerts = Object.values(CERTIFICATE_CATEGORIES).flat();
    const uncategorized = filteredCertificates.filter(cert =>
      !categorizedCerts.includes(cert) &&
      !existingCertificates.some(ec => ec.type === cert)
    );
    if (uncategorized.length > 0) {
      groups['Other'] = uncategorized;
    }

    return groups;
  }, [filteredCertificates, existingCertificates]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCertificate = (certType) => {
    setNewCertificate(prev => ({ ...prev, type: certType }));
    setSearchTerm('');
    setIsDropdownOpen(false);
    if (onClearPrefill) onClearPrefill();
  };

  const handleClearSelection = () => {
    setNewCertificate(prev => ({ ...prev, type: '' }));
    setSearchTerm('');
    setCustomCertName(''); // FT-12 Bug 4: Clear custom name
    if (onClearPrefill) onClearPrefill();
  };

  const handleSubmit = () => {
    // FT-437: Expiry date is now OPTIONAL. Only the certificate TYPE is required.
    // A blank expiry date = a permanent certificate that never expires.
    // FT-12 Bug 4: Validate custom name required when "Other" selected
    if (!newCertificate.type) {
      alert('Please select a certificate type');
      return;
    }
    if (newCertificate.type === 'Other' && !customCertName.trim()) {
      alert('Please enter a certificate name');
      return;
    }

    // FT-12 Bug 4: Use custom name as type when "Other" is selected
    const finalType = newCertificate.type === 'Other' ? customCertName.trim() : newCertificate.type;
    // FT-448 (was FT-437): Normalise a blank expiry to a clean permanent shape via the
    // shared normalizeCertificateExpiry helper (expiry_date: null + doesNotExpire: true;
    // a dated cert keeps its date + doesNotExpire: false). Using the shared helper means
    // the desktop form, the (same responsive) mobile crew flow, and the unit tests all run
    // the IDENTICAL normalisation.
    onAddCertificate({
      ...newCertificate,
      type: finalType,
      ...normalizeCertificateExpiry(newCertificate.expiry_date),
      id: Date.now()
    });

    // Reset form
    setNewCertificate({
      type: '',
      number: '',
      issue_date: '',
      expiry_date: '',
      issued_by: ''
    });
    setCustomCertName(''); // FT-12 Bug 4: Clear custom name
    if (onClearPrefill) onClearPrefill();
  };

  const handleCancel = () => {
    setNewCertificate({
      type: '',
      number: '',
      issue_date: '',
      expiry_date: '',
      issued_by: ''
    });
    setSearchTerm('');
    setIsDropdownOpen(false);
    setCustomCertName(''); // FT-12 Bug 4: Clear custom name
    if (onClearPrefill) onClearPrefill();
    if (onCancel) onCancel();
  };

  return (
    <div className="border border-gray-300 rounded-lg p-4 space-y-4">
      <h4 className="text-sm font-medium text-gray-700">Add Certificate</h4>

      {/* Searchable Certificate Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Certificate Type
        </label>

        {newCertificate.type ? (
          // Selected state
          <div className="flex items-center gap-2 p-2.5 bg-white border border-gray-300 rounded-lg">
            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
            <span className="flex-1 text-sm text-gray-900">{newCertificate.type}</span>
            <button
              type="button"
              onClick={handleClearSelection}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          // Search/select state
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Search certificates..."
                className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Dropdown */}
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {/* FT-12: Custom certificate option when search has text */}
                {searchTerm.trim() && (
                  <button
                    type="button"
                    onClick={() => handleSelectCertificate(searchTerm.trim())}
                    className="w-full px-3 py-2.5 text-left text-sm border-b border-gray-100 hover:bg-cyan-50 flex items-center gap-2"
                  >
                    <PenLine className="h-4 w-4 text-cyan-600 flex-shrink-0" />
                    <span className="text-gray-700">
                      Use custom: <span className="font-medium text-cyan-700">"{searchTerm.trim()}"</span>
                    </span>
                  </button>
                )}
                {Object.keys(groupedResults).length === 0 && !searchTerm.trim() ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No certificates found
                  </div>
                ) : (
                  Object.entries(groupedResults).map(([category, certs]) => (
                    <div key={category}>
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                        {category}
                      </div>
                      {certs.map((cert) => (
                        <button
                          key={cert}
                          type="button"
                          onClick={() => handleSelectCertificate(cert)}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700"
                        >
                          {cert}
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Certificate Details - only show when type selected */}
      {newCertificate.type && (
        <>
          {/* FT-12 Bug 4: Custom certificate name input when "Other" selected */}
          {newCertificate.type === 'Other' && (
            <div>
              <label className="block text-sm font-medium text-gray-900">
                Certificate Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customCertName}
                onChange={(e) => setCustomCertName(e.target.value)}
                placeholder="Enter the certificate name"
                className="mt-1 block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the exact name of the certificate
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-900">
                Certificate Number
              </label>
              <input
                type="text"
                value={newCertificate.number}
                onChange={(e) => setNewCertificate(prev => ({ ...prev, number: e.target.value }))}
                placeholder="e.g., UK123456"
                className="mt-1 block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">
                Issued By
              </label>
              <input
                type="text"
                value={newCertificate.issued_by}
                onChange={(e) => setNewCertificate(prev => ({ ...prev, issued_by: e.target.value }))}
                placeholder="e.g., MCA"
                className="mt-1 block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">
                Issue Date
              </label>
              <input
                type="date"
                value={newCertificate.issue_date}
                onChange={(e) => setNewCertificate(prev => ({ ...prev, issue_date: e.target.value }))}
                className="mt-1 block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">
                Expiry Date <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={newCertificate.expiry_date || ''}
                onChange={(e) => setNewCertificate(prev => ({ ...prev, expiry_date: e.target.value }))}
                className="mt-1 block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
              />
              {/* FT-437: A blank expiry means the certificate never expires. */}
              <p className="mt-1 text-xs text-gray-500">
                Leave blank for a permanent certificate that never expires.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500"
            >
              Add Certificate
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Cancel button when no type selected */}
      {!newCertificate.type && onCancel && (
        <button
          type="button"
          onClick={handleCancel}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

export default CertificateForm;
